import React, { useRef } from 'react'
import { motion } from 'framer-motion'
import { Car, Building2, GraduationCap, Heart, Target, ArrowRight, Hotel } from 'lucide-react'
import { Link } from 'react-router-dom'
import Footer from '../components/Footer'
import { useTranslation } from 'react-i18next'

const Industries = () => {
  const { t } = useTranslation()
  const containerRef = useRef(null)

  const industries = [
    {
      id: 'automobile',
      title: t('industries.automobile.title'),
      // emoji: "🚗",
      icon: <Car className="w-8 h-8" />,
      gradient: "from-blue-500 to-cyan-600",
      bgGradient: "from-blue-50 to-cyan-50",
      features: [
        t('industries.automobile.features.opportunity'),
        t('industries.automobile.features.intent'),
        t('industries.automobile.features.leads'),
        t('industries.automobile.features.connect'),
        t('industries.automobile.features.transform')
      ],
      stats: {
        engagement: "85%",
        leadQuality: "3x",
        conversion: "40%"
      },
      link: "/industries/automobile"
    },
    {
      id: 'realEstate',
      title: t('industries.realEstate.title'),
      // emoji: "🏢",
      icon: <Building2 className="w-8 h-8" />,
      gradient: "from-purple-500 to-pink-600",
      bgGradient: "from-purple-50 to-pink-50",
      features: [
        t('industries.realEstate.features.convert'),
        t('industries.realEstate.features.identify'),
        t('industries.realEstate.features.schedule'),
        t('industries.realEstate.features.power'),
        t('industries.realEstate.features.ensure')
      ],
      stats: {
        engagement: "92%",
        leadQuality: "4x",
        conversion: "55%"
      },
      link: "/industries/real-estate"
    },
    {
      id: 'education',
      title: t('industries.education.title'),
      // emoji: "🎓",
      icon: <GraduationCap className="w-8 h-8" />,
      gradient: "from-green-500 to-teal-600",
      bgGradient: "from-green-50 to-teal-50",
      features: [
        t('industries.education.features.support'),
        t('industries.education.features.personalized'),
        t('industries.education.features.access'),
        t('industries.education.features.insights'),
        t('industries.education.features.track')
      ],
      stats: {
        engagement: "92%",
        leadQuality: "4x",
        conversion: "50%"
      },
      link: "/industries/education"
    },
    {
      id: 'hospitality',
      title: t('industries.hospitality.title'),
      icon: <Hotel className="w-8 h-8" />,
      gradient: "from-amber-500 to-orange-600",
      bgGradient: "from-amber-50 to-orange-50",
      features: [
        t('industries.hospitality.features.enhance')
      ],
      stats: {
        engagement: "88%",
        leadQuality: "3.2x",
        conversion: "45%"
      },
      link: "/industries/hospitality"
    },
    {
      id: 'healthcare',
      title: t('industries.healthcare.title'),
      icon: <Heart className="w-8 h-8" />,
      gradient: "from-red-500 to-rose-600",
      bgGradient: "from-red-50 to-rose-50",
      features: [
        t('industries.healthcare.features.automate')
      ],
      stats: {
        engagement: "90%",
        leadQuality: "3.5x",
        conversion: "48%"
      },
      link: "/industries/healthcare"
    }
  ]


  return (
    <div ref={containerRef} className="min-h-screen">
      {/* Hero Section - Dark Gradient Background */}
      <section className="relative min-h-screen bg-gradient-to-br from-indigo-950 via-slate-900 to-purple-950 overflow-hidden flex items-center justify-center py-20">
        {/* Animated Background Elements */}
        <motion.div
          className="absolute top-20 left-10 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-400/30 text-purple-300 text-sm font-medium mb-8"
          >
            <Target className="w-4 h-4 mx-2" />
            {t('industries.page.badge')}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6"
          >
            {t('industries.page.mainTitle')}{' '}
            <span className="block bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
              {t('industries.page.mainSubtitle')}
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto mb-12"
          >
            {t('industries.page.mainDescription')}{' '}
            <span className="text-purple-400 font-semibold">{t('industries.page.engagement')}</span>,{' '}
            <span className="text-blue-400 font-semibold">{t('industries.page.insights')}</span>, {t('industries.page.and')}{' '}
            <span className="text-cyan-400 font-semibold">{t('industries.page.growth')}</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(147, 51, 234, 0.3)" }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              document.getElementById('industries-solutions').scrollIntoView({ 
                behavior: 'smooth' 
              });
            }}
              className="px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:shadow-2xl hover:shadow-purple-500/25 transition-all duration-300"
            >
              {t('industries.page.exploreIndustries')}
            </motion.button>

            <Link to="/inquiry">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 border-2 border-purple-500/30 text-white font-semibold rounded-xl hover:border-purple-400 hover:bg-purple-500/10 transition-all duration-300"
              >
                {t('industries.page.getStartedFree')}
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* What is Konvo AI Section - New Standalone Section */}
      <section className="relative py-24 bg-gradient-to-br from-slate-900 via-purple-900/90 to-indigo-900 overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto"
          >
            {/* Main Content Card */}
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 md:p-12 border border-white/20 shadow-2xl">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-center mb-8"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r from-purple-500 to-blue-500 mb-6 shadow-lg">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
                  {t('industries.page.notJustChatbot')}
                </h2>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.4 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                <p className="text-xl md:text-2xl text-gray-200 leading-relaxed text-center font-light">
                  {t('industries.page.notJustChatbotDesc1')}{' '}
                  <span className="text-purple-300 font-semibold">{t('industries.page.intelligentAssistant')}</span>{' '}
                  {t('industries.page.notJustChatbotDesc2')}
                </p>

                {/* Feature Grid */}
                <div className="grid md:grid-cols-2 gap-6 pt-8">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    viewport={{ once: true }}
                    className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
                  >
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                      <div className="w-2 h-2 bg-purple-400 rounded-full mx-3"></div>
                      {t('industries.page.handlesEverything')}
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      {t('industries.page.handlesEverythingDesc')}
                    </p>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                    viewport={{ once: true }}
                    className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
                  >
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                      <div className="w-2 h-2 bg-blue-400 rounded-full mx-3"></div>
                      {t('industries.page.learnsYourBusiness')}
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      {t('industries.page.learnsYourBusinessDesc')}
                    </p>
                  </motion.div>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.7 }}
                  viewport={{ once: true }}
                  className="pt-6 text-center"
                >
                  <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-purple-300 via-blue-300 to-cyan-300 bg-clip-text text-transparent">
                    {t('industries.page.scaleConversations')}
                  </p>
                </motion.div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Konvo AI Edge Section */}
      <section className="py-20 bg-gradient-to-br from-white via-purple-50/30 to-blue-50/30 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-200/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto"
          >
            {/* Header */}
            <div className="text-center mb-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                className="inline-block px-4 py-2 bg-gradient-to-r from-purple-100 to-blue-100 rounded-full mb-4"
              >
                <span className="text-sm font-semibold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                  {t('industries.page.konvoAdvantage')}
                </span>
              </motion.div>
              
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-4xl md:text-5xl font-bold mb-6"
              >
                <span className="bg-gradient-to-r from-slate-900 via-purple-900 to-slate-900 bg-clip-text text-transparent">
                  {t('industries.page.yourIndustryVoice')}
                </span>
                <br />
                <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                  {t('industries.page.yourGrowthEngine')}
                </span>
              </motion.h2>
            </div>

            {/* Content Cards */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-purple-100/50 hover:shadow-2xl transition-all duration-300"
              >
                <div className="flex items-start mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-purple-500 to-purple-600 flex items-center justify-center text-white mx-4 flex-shrink-0">
                    <Target className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{t('industries.page.seamlessIntegration')}</h3>
                    <p className="text-gray-600 leading-relaxed">
                      {t('industries.page.seamlessIntegrationDesc')}
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-blue-100/50 hover:shadow-2xl transition-all duration-300"
              >
                <div className="flex items-start mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center text-white mx-4 flex-shrink-0">
                    <ArrowRight className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{t('industries.page.intelligentAnalytics')}</h3>
                    <p className="text-gray-600 leading-relaxed">
                      {t('industries.page.intelligentAnalyticsDesc')}
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Bottom Highlight */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-8 text-center shadow-2xl"
            >
              <p className="text-xl font-semibold text-white leading-relaxed">
                {t('industries.page.focusOnWhatMatters')}
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Industries Section - Light Gray Background */}
      <section id="industries-solutions" className="py-20 bg-gradient-to-br from-slate-50 to-purple-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('industries.hero.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('industries.hero.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300"
              >
                <div className="p-8">
                  <div className="flex items-start mb-6">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${industry.gradient} flex items-center justify-center text-white flex-shrink-0`}>
                        {industry.icon}
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {industry.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {industry.features[0]}
                  </p>
                  
                  <Link to={industry.link}>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-300 bg-gradient-to-r ${industry.gradient} text-white hover:shadow-lg flex items-center justify-center space-x-2`}
                    >
                      <span>{t('industries.discoverMore')}</span>
                      <ArrowRight className="w-4 h-4" />
                    </motion.button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Dark Gradient Background */}
      <section className="relative py-20 bg-gradient-to-br from-indigo-950 via-slate-900 to-purple-950 overflow-hidden">
        {/* Animated Background Elements */}
        <motion.div
          className="absolute top-20 right-10 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-20 left-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        <div className="relative z-10 container mx-auto px-6 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            {t('industries.page.seeHowItWorks')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-xl text-gray-300 mb-10 max-w-3xl mx-auto"
          >
            {t('industries.page.seeHowItWorksDesc')}
          </motion.p>
          <Link to="/inquiry">
            <motion.button
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center gap-2 mx-auto"
            >
              <span>{t('industries.page.scheduleFreeDemo')}</span>
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default Industries